import React, { useState, useEffect } from 'react';
import { supabase } from './supabase';
import './tide.css';

function Tide() {
  var [records, setRecords] = useState([]);
  var [dailyLogs, setDailyLogs] = useState({});
  var [loading, setLoading] = useState(true);
  var [activeRecord, setActiveRecord] = useState(null);
  var [calMonth, setCalMonth] = useState(new Date());
  var [selectedDate, setSelectedDate] = useState(null);
  var [showLog, setShowLog] = useState(false);
  var [analysisText, setAnalysisText] = useState(null);
  var [analysisLoading, setAnalysisLoading] = useState(false);
  var [liveAnalysis, setLiveAnalysis] = useState(null);

  // 每日记录表单
  var [logFlow, setLogFlow] = useState('');
  var [logSymptoms, setLogSymptoms] = useState({});
  var [openSymptom, setOpenSymptom] = useState(null);
  var [logMood, setLogMood] = useState('');
  var [logNotes, setLogNotes] = useState('');
  var [logSaving, setLogSaving] = useState(false);

  useEffect(function() { fetchRecords(); }, []);

  // ========== 数据操作 ==========

  var fetchRecords = async function() {
    var result = await supabase.from('tide_records').select('*').order('start_date', { ascending: false });
    var recs = result.data || [];
    setRecords(recs);

    // 找进行中的经期（end_date 为空）
    var active = null;
    for (var i = 0; i < recs.length; i++) {
      if (!recs[i].end_date) { active = recs[i]; break; }
    }
    setActiveRecord(active);

    // 获取所有记录的每日日志
    if (recs.length > 0) {
      var ids = recs.map(function(r) { return r.id; });
      var logResult = await supabase.from('tide_daily_logs').select('*').in('tide_record_id', ids);
      var logMap = {};
      (logResult.data || []).forEach(function(l) { logMap[l.log_date] = l; });
      setDailyLogs(logMap);
    }

    setLoading(false);
  };

  // 来了
  var startPeriod = async function() {
    if (activeRecord) return;
    var today = formatDateISO(new Date());

    // 防数据污染：间隔异常时确认
    if (records.length > 0) {
      var lastStart = new Date(records[0].start_date + 'T00:00:00');
      var todayDate = new Date(); todayDate.setHours(0, 0, 0, 0);
      var gap = Math.floor((todayDate - lastStart) / 86400000);
      if (gap < 18) {
        if (!window.confirm('距离上次经期只有' + gap + '天，比平常短，确认记录吗？')) return;
      } else if (gap > 45) {
        if (!window.confirm('距离上次经期已经' + gap + '天，确认记录吗？')) return;
      }
    }

    await supabase.from('tide_records').insert([{ start_date: today, end_date: null, notes: null }]);
    fetchRecords();
  };

  // 结束了 + 触发AI分析
  var endPeriod = async function() {
    if (!activeRecord) return;
    var today = formatDateISO(new Date());
    await supabase.from('tide_records').update({ end_date: today }).eq('id', activeRecord.id);
    fetchRecords();

    // 异步触发AI月度分析
    setAnalysisLoading(true);
    setAnalysisText(null);
    try {
      var resp = await fetch('/api/tide/analysis', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      var data = await resp.json();
      if (data.analysis) setAnalysisText(data.analysis);
    } catch(e) { console.log('Analysis error:', e); }
    setAnalysisLoading(false);
  };

  // 删除记录
  var deleteRecord = async function(id) {
    await supabase.from('tide_records').delete().eq('id', id);
    fetchRecords();
  };

  // 保存每日记录
  var saveDailyLog = async function() {
    if (!selectedDate) return;
    var dateStr = formatDateISO(selectedDate);
    var record = findRecordForDate(dateStr);
    if (!record) return;

    setLogSaving(true);
    var symptomsStr = Object.keys(logSymptoms).length > 0 ? JSON.stringify(logSymptoms) : null;
    var existing = dailyLogs[dateStr];

    if (existing) {
      await supabase.from('tide_daily_logs')
        .update({ flow: logFlow || null, symptoms: symptomsStr, mood: logMood || null, notes: logNotes || null })
        .eq('id', existing.id);
    } else {
      await supabase.from('tide_daily_logs').insert([{
        tide_record_id: record.id,
        log_date: dateStr,
        flow: logFlow || null,
        symptoms: symptomsStr,
        mood: logMood || null,
        notes: logNotes || null
      }]);
    }

    setLogSaving(false);
    setShowLog(false);
    fetchRecords();

    // 保存后触发实时AI小结
    if (activeRecord) {
      fetch('/api/tide/analysis', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ live: true })
      }).then(function(r) { return r.json(); }).then(function(d) {
        if (d.analysis) setLiveAnalysis(d.analysis);
      }).catch(function() {});
    }
  };

  // ========== 工具函数 ==========

  var formatDateISO = function(d) {
    var year = d.getFullYear();
    var month = String(d.getMonth() + 1).padStart(2, '0');
    var day = String(d.getDate()).padStart(2, '0');
    return year + '-' + month + '-' + day;
  };

  var formatDate = function(dateStr) {
    if (!dateStr) return '';
    var parts = dateStr.split('-');
    return parseInt(parts[1]) + '月' + parseInt(parts[2]) + '日';
  };

  // 找某个日期属于哪条经期记录
  var findRecordForDate = function(dateStr) {
    for (var i = 0; i < records.length; i++) {
      var start = records[i].start_date;
      var end = records[i].end_date || formatDateISO(new Date());
      if (dateStr >= start && dateStr <= end) return records[i];
    }
    return null;
  };

  // 是否是经期日
  var isPeriodDay = function(date) {
    return !!findRecordForDate(formatDateISO(date));
  };

  // ========== Robust统计工具 ==========
  // 基于 Bull et al. 2019 (NPJ Digital Medicine, 612,613周期)
  // 和 Scientific Reports 2021 贝叶斯状态空间模型的简化实现

  var calcMedian = function(arr) {
    var s = arr.slice().sort(function(a, b) { return a - b; });
    if (s.length % 2 === 0) return (s[s.length / 2 - 1] + s[s.length / 2]) / 2;
    return s[Math.floor(s.length / 2)];
  };

  var calcMAD = function(arr, median) {
    var deviations = arr.map(function(v) { return Math.abs(v - median); });
    return calcMedian(deviations);
  };

  // 预测 — Robust统计版
  // 中位数替代均值（抗异常值），MAD计算置信区间，人群先验混合（小样本兜底）
  var getPrediction = function() {
    if (records.length < 2) return null;

    // 提取周期长度（records按start_date降序，cycles[0]是最近的间隔）
    var allCycles = [];
    var durations = [];
    for (var i = 0; i < records.length - 1; i++) {
      var diff = Math.floor(
        (new Date(records[i].start_date + 'T00:00:00') - new Date(records[i + 1].start_date + 'T00:00:00')) / 86400000
      );
      if (diff > 0) allCycles.push(diff);
    }
    for (var j = 0; j < records.length; j++) {
      if (records[j].end_date) {
        var dur = Math.ceil(
          (new Date(records[j].end_date + 'T00:00:00') - new Date(records[j].start_date + 'T00:00:00')) / 86400000
        ) + 1;
        durations.push(dur);
      }
    }
    if (allCycles.length === 0) return null;

    // 过滤异常值：<18天或>45天不纳入预测（防数据污染）
    var validCycles = allCycles.filter(function(c) { return c >= 18 && c <= 45; });
    if (validCycles.length === 0) validCycles = allCycles; // 全部异常则保留全部

    // 取最近8个有效周期
    var recentCycles = validCycles.slice(0, 8);

    // 中位数（比均值更robust，一两个异常值拉不动它）
    var median = calcMedian(recentCycles);

    // 人群先验混合（Bull et al. 2019: 61万+周期平均29.3天）
    // 数据越多先验权重越低，≥6个周期时完全依赖个人数据
    var PRIOR_CYCLE = 29.3;
    var priorWeight = Math.max(0, (6 - recentCycles.length) / 6);
    var robustCycle = Math.round(median * (1 - priorWeight) + PRIOR_CYCLE * priorWeight);

    // MAD（Median Absolute Deviation）→ 置信区间
    // MAD × 1.4826 ≈ 标准差（正态分布下等价）
    var mad = calcMAD(recentCycles, median);
    var sigmaEquiv = 1.4826 * mad;
    var ciDays = Math.max(1, Math.round(sigmaEquiv)); // 至少±1天

    // 趋势检测（≥4个周期才有意义，简单线性回归）
    var trend = 0;
    var trendLabel = '';
    if (recentCycles.length >= 4) {
      var n = recentCycles.length;
      var reversed = recentCycles.slice().reverse(); // 从旧到新
      var sumX = 0, sumY = 0, sumXY = 0, sumXX = 0;
      for (var t = 0; t < n; t++) {
        sumX += t; sumY += reversed[t]; sumXY += t * reversed[t]; sumXX += t * t;
      }
      trend = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX);
      if (trend > 0.5) trendLabel = '周期在变长';
      else if (trend < -0.5) trendLabel = '周期在变短';
      else trendLabel = '周期稳定';
    }

    // 变异系数 → 稳定度百分比
    // CV = MAD / median × 100%，稳定度 = 100% - 归一化CV
    var cv = median > 0 ? (mad / median * 100) : 0;
    var stability = Math.max(0, Math.min(100, Math.round(100 - cv * 1.4826)));

    // 经期持续天数中位数
    var avgDuration = 5;
    if (durations.length > 0) avgDuration = Math.round(calcMedian(durations));

    // 预测日期
    var lastStart = new Date(records[0].start_date + 'T00:00:00');
    var nextDate = new Date(lastStart.getTime() + robustCycle * 86400000);
    // 有明显趋势时保守微调（趋势斜率×0.5，防过度矫正）
    if (Math.abs(trend) > 0.5 && recentCycles.length >= 4) {
      nextDate = new Date(nextDate.getTime() + Math.round(trend * 0.5) * 86400000);
    }

    var today = new Date(); today.setHours(0, 0, 0, 0);
    var daysLeft = Math.ceil((nextDate - today) / 86400000);
    var ciStart = new Date(nextDate.getTime() - ciDays * 86400000);
    var ciEnd = new Date(nextDate.getTime() + ciDays * 86400000);

    return {
      daysLeft: daysLeft, avgCycle: robustCycle, avgDuration: avgDuration, nextDate: nextDate,
      ciDays: ciDays, ciStart: ciStart, ciEnd: ciEnd,
      stability: stability, trend: trend, trendLabel: trendLabel,
      cycleCount: recentCycles.length,
      ovulationDate: new Date(nextDate.getTime() - 14 * 86400000),
      ovulationStart: new Date(nextDate.getTime() - 17 * 86400000),
      ovulationEnd: new Date(nextDate.getTime() - 13 * 86400000)
    };
  };

  // 是否是排卵日（那一天）
  var isOvulationDay = function(date) {
    var pred = getPrediction();
    if (!pred) return false;
    return formatDateISO(date) === formatDateISO(pred.ovulationDate);
  };

  // 是否在排卵期（排卵日前3天到后1天）
  var isOvulationPeriod = function(date) {
    var pred = getPrediction();
    if (!pred) return false;
    var dateStr = formatDateISO(date);
    return dateStr >= formatDateISO(pred.ovulationStart) && dateStr <= formatDateISO(pred.ovulationEnd) && !isOvulationDay(date);
  };

  // 当前周期阶段
  var getCurrentPhase = function() {
    var pred = getPrediction();
    var today = new Date(); today.setHours(0, 0, 0, 0);
    if (activeRecord) return { name: '月经期', icon: '🌊', tip: '注意保暖，多休息', color: '#E87981' };
    if (!pred || !records[0]) return null;
    var lastEnd = records[0].end_date;
    if (!lastEnd) return null;
    var lastEndDate = new Date(lastEnd + 'T00:00:00');
    var ovDate = pred.ovulationDate;
    var daysSinceEnd = Math.floor((today - lastEndDate) / 86400000);
    var daysToOvulation = Math.floor((ovDate - today) / 86400000);
    if (daysToOvulation > 3) return { name: '卵泡期', icon: '🌱', tip: '精力充沛，适合运动', color: '#7BB88A' };
    if (daysToOvulation >= -1 && daysToOvulation <= 3) return { name: '排卵期', icon: '✨', tip: '注意身体变化', color: '#C49ADE' };
    return { name: '黄体期', icon: '🍂', tip: '可能会有情绪波动', color: '#D4A574' };
  };

  // 是否是预测日
  var isPredictedDay = function(date) {
    var pred = getPrediction();
    if (!pred || activeRecord) return false;
    var dateStr = formatDateISO(date);
    var predStart = formatDateISO(pred.nextDate);
    var predEndDate = new Date(pred.nextDate.getTime() + (pred.avgDuration - 1) * 86400000);
    var predEnd = formatDateISO(predEndDate);
    return dateStr >= predStart && dateStr <= predEnd;
  };

  // 是否是今天
  var isToday = function(date) {
    var t = new Date(); t.setHours(0, 0, 0, 0);
    var d = new Date(date); d.setHours(0, 0, 0, 0);
    return t.getTime() === d.getTime();
  };

  // ========== 日历 ==========

  var getCalendarDays = function() {
    var year = calMonth.getFullYear();
    var month = calMonth.getMonth();
    var firstDay = new Date(year, month, 1).getDay();
    var daysInMonth = new Date(year, month + 1, 0).getDate();
    var days = [];
    for (var i = 0; i < firstDay; i++) { days.push(null); }
    for (var d = 1; d <= daysInMonth; d++) { days.push(new Date(year, month, d)); }
    return days;
  };

  var prevMonth = function() {
    setCalMonth(new Date(calMonth.getFullYear(), calMonth.getMonth() - 1, 1));
    setShowLog(false);
    setSelectedDate(null);
  };

  var nextMonth = function() {
    setCalMonth(new Date(calMonth.getFullYear(), calMonth.getMonth() + 1, 1));
    setShowLog(false);
    setSelectedDate(null);
  };

  var handleDateClick = function(date) {
    if (!date) return;
    var dateStr = formatDateISO(date);
    setSelectedDate(date);

    if (isPeriodDay(date)) {
      var log = dailyLogs[dateStr];
      if (log) {
        setLogFlow(log.flow || '');
        try {
          var parsed = log.symptoms ? JSON.parse(log.symptoms) : {};
          if (Array.isArray(parsed)) {
            var obj = {}; parsed.forEach(function(s) { obj[s] = 1; }); setLogSymptoms(obj);
          } else { setLogSymptoms(parsed || {}); }
        } catch (e) { setLogSymptoms({}); }
        setLogMood(log.mood || '');
        setLogNotes(log.notes || '');
      } else {
        setLogFlow('');
        setLogSymptoms({});
        setLogMood('');
        setLogNotes('');
      }
      setShowLog(true);
    } else {
      setShowLog(false);
    }
  };

  var toggleSymptom = function(key) {
    if (logSymptoms[key]) {
      // 已选中 → 取消
      var next = Object.assign({}, logSymptoms);
      delete next[key];
      setLogSymptoms(next);
      setOpenSymptom(null);
    } else {
      // 未选中 → 展开圆点面板
      setOpenSymptom(openSymptom === key ? null : key);
    }
  };

  var setSymptomLevel = function(key, level) {
    var next = Object.assign({}, logSymptoms);
    next[key] = level;
    setLogSymptoms(next);
    setOpenSymptom(null);
  };

  // ========== 洞察统计 ==========

  var getInsights = function() {
    var finishedRecs = records.filter(function(r) { return r.end_date; });
    if (finishedRecs.length < 1) return null;

    // 周期长度（带月份标签）
    var cycleData = [];
    for (var i = 0; i < records.length - 1; i++) {
      var diff = Math.floor((new Date(records[i].start_date + 'T00:00:00') - new Date(records[i + 1].start_date + 'T00:00:00')) / 86400000);
      if (diff > 0 && diff >= 18 && diff <= 45) {
        var m = new Date(records[i + 1].start_date).getMonth() + 1;
        cycleData.push({ days: diff, label: m + '月' });
      }
    }
    cycleData.reverse(); // 从旧到新
    var recentCycleData = cycleData.slice(-6);

    // 经期持续天数
    var durs = finishedRecs.map(function(r) {
      return Math.ceil((new Date(r.end_date + 'T00:00:00') - new Date(r.start_date + 'T00:00:00')) / 86400000) + 1;
    });

    // 中位数
    var medCycle = 0, medDur = 0;
    if (recentCycleData.length > 0) {
      var sc = recentCycleData.map(function(c) { return c.days; }).sort(function(a, b) { return a - b; });
      medCycle = sc.length % 2 === 0 ? Math.round((sc[sc.length / 2 - 1] + sc[sc.length / 2]) / 2) : sc[Math.floor(sc.length / 2)];
    }
    if (durs.length > 0) {
      var sd = durs.slice().sort(function(a, b) { return a - b; });
      medDur = sd.length % 2 === 0 ? Math.round((sd[sd.length / 2 - 1] + sd[sd.length / 2]) / 2) : sd[Math.floor(sd.length / 2)];
    }

    // 波动范围
    var maxC = 0, minC = 999;
    recentCycleData.forEach(function(c) { if (c.days > maxC) maxC = c.days; if (c.days < minC) minC = c.days; });
    var fluctuation = recentCycleData.length >= 2 ? Math.round((maxC - minC) / 2 * 10) / 10 : 0;

    // 症状频率（按经期次数统计，不是日志次数）
    var symByPeriod = {};
    Object.keys(dailyLogs).forEach(function(dateKey) {
      var log = dailyLogs[dateKey];
      if (log.symptoms && log.tide_record_id) {
        try {
          var syms = JSON.parse(log.symptoms);
          if (!symByPeriod[log.tide_record_id]) symByPeriod[log.tide_record_id] = {};
          if (Array.isArray(syms)) {
            syms.forEach(function(s) { symByPeriod[log.tide_record_id][s] = true; });
          } else {
            Object.keys(syms).forEach(function(s) { symByPeriod[log.tide_record_id][s] = true; });
          }
        } catch(e) {}
      }
    });
    var symCount = {};
    Object.values(symByPeriod).forEach(function(syms) {
      Object.keys(syms).forEach(function(s) { symCount[s] = (symCount[s] || 0) + 1; });
    });
    var totalPeriods = Math.max(1, finishedRecs.length);
    var symLabels = {cramps:'痉挛',headache:'头痛',backache:'腰痛',breast:'胸胀',fatigue:'疲劳',bloating:'浮肿',nausea:'恶心',diarrhea:'腹泻',insomnia:'失眠',mood_swings:'情绪波动',appetite:'食欲变化',gas:'胀气'};
    var symTop = Object.keys(symCount).sort(function(a, b) { return symCount[b] - symCount[a]; }).slice(0, 5)
      .map(function(key) { return { key: key, label: symLabels[key] || key, count: symCount[key], total: totalPeriods }; });

    return { cycleData: recentCycleData, medCycle: medCycle, medDur: medDur, fluctuation: fluctuation, symTop: symTop, totalPeriods: totalPeriods };
  };

  // ========== 常量 ==========

  var SYMPTOM_ICONS = {
    cramps: '<path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>',
    headache: '<circle cx="12" cy="8" r="5"/><path d="M12 13v4"/><path d="M9 19h6"/>',
    backache: '<path d="M12 3v18"/><path d="M7 8c2 1.5 8 1.5 10 0"/><path d="M7 16c2-1.5 8-1.5 10 0"/>',
    breast: '<path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78L12 21.23l8.84-8.84a5.5 5.5 0 000-7.78z"/>',
    fatigue: '<path d="M17 8h1a4 4 0 010 8h-1"/><path d="M3 8h14v9a4 4 0 01-4 4H7a4 4 0 01-4-4V8z"/><path d="M6 2v3M10 2v3M14 2v3"/>',
    bloating: '<circle cx="12" cy="11" r="8"/><path d="M12 19v2"/><path d="M9 22h6"/>',
    nausea: '<path d="M2 12c2-3 4-3 6 0s4 3 6 0 4-3 6 0"/>',
    diarrhea: '<path d="M12 2c0 0-6 7-6 12a6 6 0 0012 0c0-5-6-12-6-12z"/>',
    insomnia: '<path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z"/>',
    mood_swings: '<circle cx="12" cy="12" r="9"/><path d="M8 9h.01M16 9h.01"/><path d="M8 15s1.5 2 4 2 4-2 4-2"/>',
    appetite: '<path d="M4 2v20M4 8h3a3 3 0 010 6H4"/><path d="M20 2v6c0 3-2 4-4 4s-4-1-4-4V2M16 2v6"/>',
    gas: '<ellipse cx="12" cy="14" rx="8" ry="7"/><circle cx="9" cy="6" r="1.5"/><circle cx="13" cy="4" r="1"/><circle cx="11" cy="1.5" r=".7"/>'
  };

  var SYMPTOMS = [
    { key: 'cramps', label: '痉挛' },
    { key: 'headache', label: '头痛' },
    { key: 'backache', label: '腰痛' },
    { key: 'breast', label: '胸胀' },
    { key: 'fatigue', label: '疲劳' },
    { key: 'bloating', label: '浮肿' },
    { key: 'nausea', label: '恶心' },
    { key: 'diarrhea', label: '腹泻' },
    { key: 'insomnia', label: '失眠' },
    { key: 'mood_swings', label: '情绪波动' },
    { key: 'appetite', label: '食欲变化' },
    { key: 'gas', label: '胀气' }
  ];

  var MOODS = [
    { key: 'happy', label: '开心', color: '#E2A84B', bg: '#FDF3E0' },
    { key: 'calm', label: '平静', color: '#7BB88A', bg: '#E8F5EC' },
    { key: 'sweet', label: '甜甜的', color: '#E8A0B0', bg: '#FCEEF2' },
    { key: 'secure', label: '安心', color: '#B89878', bg: '#F5EDE3' },
    { key: 'sad', label: '低落', color: '#8BA4BE', bg: '#E8EFF5' },
    { key: 'tearful', label: '想哭', color: '#7BA4C4', bg: '#E5EFF8' },
    { key: 'anxious', label: '焦虑', color: '#D4956B', bg: '#FAF0E6' },
    { key: 'irritable', label: '烦躁', color: '#E87981', bg: '#FCEAE8' },
    { key: 'sensitive', label: '敏感', color: '#C48AB0', bg: '#F5E8F0' },
    { key: 'tired', label: '疲惫', color: '#A489BD', bg: '#F0EAF5' },
    { key: 'numb', label: '无感', color: '#A0A098', bg: '#F0EFED' },
    { key: 'helpless', label: '无奈', color: '#8C9AAB', bg: '#E9EDF2' }
  ];

  var WEEKDAYS = ['日', '一', '二', '三', '四', '五', '六'];
  var FLOWS = [
    { key: 'light', label: '轻' },
    { key: 'medium', label: '中' },
    { key: 'heavy', label: '重' }
  ];

  // ========== 渲染 ==========

  var prediction = getPrediction();
  var phase = getCurrentPhase();
  var insights = getInsights();
  var activeDays = activeRecord
    ? Math.floor((new Date() - new Date(activeRecord.start_date + 'T00:00:00')) / 86400000) + 1
    : 0;

  // 小组件数据
  var streakDays = 0;
  var moodSpectrum = [];
  var moodColorsMap = {happy:'#E2A84B',calm:'#7BB88A',sad:'#8BA4BE',irritable:'#E87981',tired:'#A489BD',sweet:'#E8A0B0',secure:'#B89878',tearful:'#7BA4C4',anxious:'#D4956B',sensitive:'#C48AB0',numb:'#A0A098',helpless:'#8C9AAB'};
  var moodNamesMap = {happy:'开心',calm:'平静',sad:'低落',irritable:'烦躁',tired:'疲惫',sweet:'甜甜的',secure:'安心',tearful:'想哭',anxious:'焦虑',sensitive:'敏感',numb:'无感',helpless:'无奈'};
  if (activeRecord) {
    Object.keys(dailyLogs).forEach(function(dk) {
      if (dailyLogs[dk].tide_record_id === activeRecord.id) streakDays++;
    });
    Object.keys(dailyLogs).sort().forEach(function(dk) {
      var lg = dailyLogs[dk];
      if (lg.tide_record_id === activeRecord.id && lg.mood) {
        moodSpectrum.push({ key: lg.mood, color: moodColorsMap[lg.mood] || '#C4B8AE', label: moodNamesMap[lg.mood] || lg.mood });
      }
    });
  }

  if (loading) {
    return <div className="tide-page"><div className="tide-loading">加载中...</div></div>;
  }

  return (
    <div className="tide-page">

      {/* 导航栏 */}
      <div className="tide-header">
        <button className="tide-back" onClick={function() { window.history.back(); }}>‹ 返回</button>
        <div className="tide-title">潮汐</div>
        <div style={{ width: 60 }}></div>
      </div>

      {/* 状态卡片 */}
      <div className="tide-status-card" style={phase ? {background: 'linear-gradient(135deg, #FFFDF9 0%, ' + phase.color + '08 100%)'} : {}}>
        {!activeRecord && prediction ? (
          <div className="tide-ring-wrap">
            <svg width="64" height="64" viewBox="0 0 64 64">
              <circle cx="32" cy="32" r="27" fill="none" stroke="#F0E8E0" strokeWidth="5"/>
              <circle cx="32" cy="32" r="27" fill="none" stroke="#E87981" strokeWidth="5" strokeLinecap="round" style={{transform:'rotate(-90deg)',transformOrigin:'center'}} strokeDasharray="169.6" strokeDashoffset={169.6 * (1 - prediction.stability / 100)}/>
            </svg>
            <div className="tide-ring-val">{prediction.stability}%</div>
            <div className="tide-ring-label">稳定度</div>
          </div>
        ) : (
          <div className="tide-status-icon">{phase ? phase.icon : '🌊'}</div>
        )}

        {activeRecord ? (
          <>
            <div className="tide-status-active">
              <div className="tide-status-day">第 {activeDays} 天</div>
              <div className="tide-status-since">{formatDate(activeRecord.start_date)} 开始</div>
              <div className="tide-phase-badge" style={{background: 'rgba(232,121,129,0.1)', borderColor: 'rgba(232,121,129,0.2)'}}>
                <span className="tide-phase-name" style={{color: '#E87981'}}>月经期</span>
                <span className="tide-phase-sep">·</span>
                <span className="tide-phase-desc">注意保暖多休息</span>
              </div>
            </div>
            <button className="tide-end-btn" onClick={endPeriod}>结束了</button>
          </>
        ) : (
          <>
            {prediction ? (
              <div className="tide-status-pred">
                <div className="tide-pred-days">{prediction.daysLeft > 0 ? prediction.daysLeft : '今天'}</div>
                <div className="tide-pred-label">{prediction.daysLeft > 0 ? '天后预计到来' : '预计今天到来'}</div>
                <div className="tide-pred-detail">
                  预计 {formatDate(formatDateISO(prediction.ciStart))} — {formatDate(formatDateISO(prediction.ciEnd))}（前后浮动{prediction.ciDays}天）
                </div>
                <div className="tide-pred-stats">
                  周期中位数 {prediction.avgCycle} 天 · 稳定度 {prediction.stability}%{prediction.trendLabel ? ' · ' + prediction.trendLabel : ''}
                </div>
                {phase && (
                  <div className="tide-phase-badge" style={{background: phase.color + '12', borderColor: phase.color + '30'}}>
                    <span className="tide-phase-name" style={{color: phase.color}}>{phase.name}</span>
                    <span className="tide-phase-sep">·</span>
                    <span className="tide-phase-desc">{phase.tip}</span>
                  </div>
                )}
              </div>
            ) : (
              <div className="tide-status-pred">
                <div className="tide-pred-days">--</div>
                <div className="tide-pred-label">记录两次以上后可预测</div>
              </div>
            )}
            <button className="tide-start-btn" onClick={startPeriod}>来了</button>
          </>
        )}
      </div>

      {/* 速览小组件 */}
      {(activeRecord || prediction) && (
        <div className="tide-widgets">
          <div className="tide-widgets-grid">
            {activeRecord ? (
              <div className="tide-w tide-w-rose">
                <div className="tide-w-label">经期进行中</div>
                <div className="tide-w-num">{activeDays}<span className="tide-w-unit"> / {prediction ? prediction.avgDuration : '?'}天</span></div>
                <div className="tide-w-sub">{prediction && activeDays >= prediction.avgDuration ? '预计今天结束' : prediction ? '预计还有' + (prediction.avgDuration - activeDays) + '天' : ''}</div>
              </div>
            ) : (
              <div className="tide-w tide-w-lavender">
                <div className="tide-w-label">下次经期</div>
                <div className="tide-w-num">{prediction.daysLeft > 0 ? prediction.daysLeft : '今天'}<span className="tide-w-unit">{prediction.daysLeft > 0 ? ' 天后' : ''}</span></div>
                <div className="tide-w-sub">约 {formatDate(formatDateISO(prediction.nextDate))}</div>
              </div>
            )}

            {activeRecord && streakDays > 0 && (
              <div className="tide-w tide-w-mint">
                <div className="tide-w-label">已记录</div>
                <div className="tide-w-num">{streakDays}<span className="tide-w-unit"> 天</span></div>
                <div className="tide-w-sub">{streakDays >= 3 ? '坚持记录真棒' : '继续加油'}</div>
              </div>
            )}

            {prediction && (
              <div className="tide-w tide-w-warm">
                <div className="tide-w-label">周期稳定度</div>
                <div className="tide-w-num">{prediction.stability}<span className="tide-w-unit">%</span></div>
                <div className="tide-w-sub">{prediction.trendLabel || '稳定'}</div>
              </div>
            )}
          </div>

          {activeRecord && moodSpectrum.length >= 1 && (
            <div className="tide-w-mood-bar-wrap">
              <div className="tide-w-mood-label">情绪色谱</div>
              <div className="tide-w-mood-bar">
                {moodSpectrum.map(function(ms, mi) {
                  return <div key={mi} className="tide-w-mood-seg" style={{background: ms.color, flex: 1}}></div>;
                })}
              </div>
              <div className="tide-w-mood-legend">
                {moodSpectrum.filter(function(ms, mi, arr) { return arr.findIndex(function(m) { return m.key === ms.key; }) === mi; }).map(function(ms) {
                  return <span key={ms.key} className="tide-w-mood-leg"><span className="tide-w-mood-dot" style={{background: ms.color}}></span>{ms.label}</span>;
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* 日历 */}
      <div className="tide-calendar">
        <div className="tide-cal-header">
          <button className="tide-cal-nav" onClick={prevMonth}>‹</button>
          <div className="tide-cal-title">{calMonth.getFullYear()}年{calMonth.getMonth() + 1}月</div>
          <button className="tide-cal-nav" onClick={nextMonth}>›</button>
        </div>

        <div className="tide-cal-weekdays">
          {WEEKDAYS.map(function(w) { return <div key={w} className="tide-cal-weekday">{w}</div>; })}
        </div>

        <div className="tide-cal-grid">
          {getCalendarDays().map(function(date, idx) {
            if (!date) return <div key={'e' + idx} className="tide-cal-cell empty"></div>;

            var dateStr = formatDateISO(date);
            var period = isPeriodDay(date);
            var predicted = isPredictedDay(date);
            var today = isToday(date);
            var selected = selectedDate && dateStr === formatDateISO(selectedDate);
            var hasLog = !!dailyLogs[dateStr];
            var ovulation = isOvulationDay(date);
            var ovulationPeriod = isOvulationPeriod(date);

            var cls = 'tide-cal-cell';
            if (period) cls += ' period';
            if (predicted && !period) cls += ' predicted';
            if (ovulation && !period) cls += ' ovulation';
            if (ovulationPeriod && !period && !predicted) cls += ' ovulation-period';
            if (today) cls += ' today';
            if (selected) cls += ' selected';

            return (
              <div key={dateStr} className={cls} onClick={function() { handleDateClick(date); }}>
                <span>{date.getDate()}</span>
                {hasLog && <div className="tide-cal-dot"></div>}
              </div>
            );
          })}
        </div>

        <div className="tide-cal-legend">
          <span className="tide-legend-item"><span className="tide-legend-dot period"></span>经期</span>
          <span className="tide-legend-item"><span className="tide-legend-dot predicted"></span>预测</span>
          <span className="tide-legend-item"><span className="tide-legend-dot ovulation-period"></span>排卵期</span>
          <span className="tide-legend-item"><span className="tide-legend-dot ovulation"></span>排卵日</span>
        </div>
      </div>

      {/* 经期进行中的AI小结 */}
      {activeRecord && liveAnalysis && (
        <div className="tide-live-summary">
          <div className="tide-live-title">本次小结</div>
          <div className="tide-live-text">{liveAnalysis}</div>
        </div>
      )}

      {/* 每日记录面板 */}
      {showLog && selectedDate && isPeriodDay(selectedDate) && (function() {
        var selRecord = findRecordForDate(formatDateISO(selectedDate));
        var isFinished = selRecord && selRecord.end_date;
        var flowLabels = { light: '轻', medium: '中', heavy: '重' };
        var symptomLabels = {};
        SYMPTOMS.forEach(function(s) { symptomLabels[s.key] = s.label; });
        var moodMap = {};
        MOODS.forEach(function(m) { moodMap[m.key] = m; });

        if (isFinished) {
          return (
            <div className="tide-daily-log tide-log-readonly">
              <div className="tide-log-title">{formatDate(formatDateISO(selectedDate))} 的记录</div>
              {logFlow && <div className="tide-readonly-row"><span className="tide-readonly-label">流量</span><span className="tide-readonly-value">{flowLabels[logFlow] || logFlow}</span></div>}
              {Object.keys(logSymptoms).length > 0 && <div className="tide-readonly-row"><span className="tide-readonly-label">症状</span><span className="tide-readonly-tags">{Object.keys(logSymptoms).map(function(k) { var lvl = logSymptoms[k]; var lbl = symptomLabels[k] || k; return lbl + (lvl > 1 ? (lvl === 2 ? '(中)' : '(重)') : ''); }).join('、')}</span></div>}
              {logMood && moodMap[logMood] && <div className="tide-readonly-row"><span className="tide-readonly-label">情绪</span><span className="tide-readonly-value" style={{color: moodMap[logMood].color}}>● {moodMap[logMood].label}</span></div>}
              {logNotes && <div className="tide-readonly-note">{logNotes}</div>}
              {!logFlow && Object.keys(logSymptoms).length === 0 && !logMood && !logNotes && <div className="tide-readonly-empty">这天没有记录</div>}
            </div>
          );
        }

        return (
        <div className="tide-daily-log">
          <div className="tide-log-title">{formatDate(formatDateISO(selectedDate))} 的记录</div>

          <div className="tide-log-section">
            <div className="tide-log-label">流量</div>
            <div className="tide-log-options">
              {FLOWS.map(function(f) {
                var isActive = logFlow === f.key;
                var dropColor = isActive ? '#fff' : '#D4A49A';
                var dropPath = 'M12 2C12 2 4 11 4 17a8 8 0 0016 0C20 11 12 2 12 2Z';
                var dropFill = f.key === 'light' ? 'none' : f.key === 'heavy' ? dropColor : undefined;
                return (
                  <button key={f.key}
                    className={'tide-flow-btn' + (isActive ? ' active' : '')}
                    onClick={function() { setLogFlow(logFlow === f.key ? '' : f.key); }}>
                    {f.key === 'medium' ? (
                      <svg width="22" height="28" viewBox="0 0 24 24" fill="none">
                        <defs><linearGradient id={'drop-' + (isActive?'a':'i')} x1="0" y1="0" x2="0" y2="1"><stop offset="45%" stopColor="transparent"/><stop offset="45%" stopColor={dropColor}/></linearGradient></defs>
                        <path d={dropPath} fill={'url(#drop-' + (isActive?'a':'i') + ')'} stroke={dropColor} strokeWidth="1.5"/>
                      </svg>
                    ) : (
                      <svg width="22" height="28" viewBox="0 0 24 24" fill="none">
                        <path d={dropPath} fill={dropFill} stroke={dropColor} strokeWidth="1.5"/>
                      </svg>
                    )}
                    <span className="tide-flow-label">{f.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="tide-log-section">
            <div className="tide-log-label">症状</div>
            <div className="tide-log-tags">
              {SYMPTOMS.map(function(s) {
                var level = logSymptoms[s.key] || 0;
                var isOpen = openSymptom === s.key;
                return (
                  <div key={s.key} className="tide-sym-wrap">
                    <button
                      className={'tide-tag' + (level === 1 ? ' level-1' : level === 2 ? ' level-2' : level === 3 ? ' level-3' : '')}
                      onClick={function() { toggleSymptom(s.key); }}>
                      <svg className="tide-tag-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" dangerouslySetInnerHTML={{__html: SYMPTOM_ICONS[s.key] || ''}} />
                      {s.label}
                    </button>
                    <div className={'tide-dots' + (isOpen ? ' open' : '')}>
                      {['轻','中','重'].map(function(lbl, i) {
                        return (
                          <div key={i}
                            className={'tide-dot' + (level === i + 1 ? ' sel' : '')}
                            onClick={function() { setSymptomLevel(s.key, i + 1); }}>
                            {lbl}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="tide-log-section">
            <div className="tide-log-label">情绪</div>
            <div className="tide-mood-pills">
              {MOODS.map(function(m) {
                var active = logMood === m.key;
                return (
                  <button key={m.key}
                    className={'tide-mood-pill' + (active ? ' active' : '')}
                    style={active ? {background: m.bg, borderColor: m.color} : {}}
                    onClick={function() { setLogMood(logMood === m.key ? '' : m.key); }}>
                    <span className="tide-mood-dot" style={{background: m.color, width: active ? 12 : 10, height: active ? 12 : 10}}></span>
                    <span className="tide-mood-text" style={active ? {color: m.color} : {}}>{m.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="tide-log-section">
            <textarea className="tide-log-input" placeholder="写点什么…" rows="2"
              value={logNotes} onChange={function(e) { setLogNotes(e.target.value); }} />
          </div>

          <button className="tide-log-save" onClick={saveDailyLog} disabled={logSaving}>
            {logSaving ? '保存中...' : '保存'}
          </button>
        </div>
        );
      })()}

      {/* 周期洞察 */}
      {insights && (
        <div className="tide-insights">
          <div className="tide-insights-title">周期洞察</div>

          {/* 数据概览 */}
          <div className="tide-insights-card">
            <div className="tide-insights-label">数据概览</div>
            <div className="tide-stat-row">
              <div className="tide-stat-box">
                <div className="tide-stat-num">{insights.medCycle || '--'}</div>
                <div className="tide-stat-label">平均周期</div>
                <div className="tide-stat-sub">中位数 · 天</div>
              </div>
              <div className="tide-stat-box">
                <div className="tide-stat-num">{insights.medDur || '--'}</div>
                <div className="tide-stat-label">平均经期</div>
                <div className="tide-stat-sub">中位数 · 天</div>
              </div>
            </div>

            {/* 周期柱状图 */}
            {insights.cycleData.length >= 2 && (
              <>
                <div className="tide-insights-label">近{insights.cycleData.length}次周期长度</div>
                <div className="tide-chart-area">
                  {insights.cycleData.map(function(c, idx) {
                    var maxH = 60;
                    var minD = 20, maxD = 40;
                    var h = Math.max(20, Math.min(maxH, (c.days - minD) / (maxD - minD) * maxH));
                    return (
                      <div key={idx} className="tide-chart-bar-wrap">
                        <div className="tide-chart-val">{c.days}</div>
                        <div className="tide-chart-bar" style={{height: h + 'px'}}></div>
                        <div className="tide-chart-label">{c.label}</div>
                      </div>
                    );
                  })}
                </div>
                <div className="tide-trend-row">
                  <span className={'tide-trend-arrow' + (prediction && prediction.trendLabel === '周期在变长' ? ' longer' : prediction && prediction.trendLabel === '周期在变短' ? ' shorter' : ' stable')}>
                    {prediction && prediction.trendLabel === '周期在变长' ? '↑ 变长' : prediction && prediction.trendLabel === '周期在变短' ? '↓ 变短' : '→ 稳定'}
                  </span>
                  <span className="tide-trend-text">波动 ±{insights.fluctuation}天</span>
                </div>
              </>
            )}
          </div>

          {/* 症状频率 */}
          {insights.symTop.length > 0 && (
            <div className="tide-insights-card">
              <div className="tide-insights-label">常见症状 TOP {insights.symTop.length}</div>
              {insights.symTop.map(function(s) {
                var pct = Math.round(s.count / s.total * 100);
                return (
                  <div key={s.key} className="tide-sym-freq">
                    <span className="tide-sym-name">{s.label}</span>
                    <div className="tide-sym-bar-bg"><div className="tide-sym-bar-fill" style={{width: pct + '%'}}></div></div>
                    <span className="tide-sym-count">{s.count}/{s.total}</span>
                  </div>
                );
              })}
            </div>
          )}

          {/* AI分析 */}
          {(analysisLoading || analysisText) && (
            <div className="tide-insights-card tide-ai-card">
              <div className="tide-ai-title">哥哥的分析</div>
              {analysisLoading ? (
                <div className="tide-analysis-loading">正在分析...</div>
              ) : (
                <div className="tide-ai-text">{analysisText}</div>
              )}
              {analysisText && <button className="tide-analysis-close" onClick={function() { setAnalysisText(null); }}>收起</button>}
            </div>
          )}
        </div>
      )}

      {/* 历史记录 */}
      <div className="tide-history">
        <div className="tide-history-title">历史记录</div>
        {records.length === 0 ? (
          <div className="tide-empty">还没有记录</div>
        ) : (
          records.map(function(r) {
            var duration = '';
            if (r.end_date) {
              var days = Math.ceil(
                (new Date(r.end_date + 'T00:00:00') - new Date(r.start_date + 'T00:00:00')) / 86400000
              ) + 1;
              duration = days + '天';
            }
            // 提取这条记录的流量数据
            var flowLogs = [];
            Object.keys(dailyLogs).forEach(function(dk) {
              if (dailyLogs[dk].tide_record_id === r.id && dailyLogs[dk].flow) {
                flowLogs.push({ date: dk, flow: dailyLogs[dk].flow });
              }
            });
            flowLogs.sort(function(a, b) { return a.date.localeCompare(b.date); });

            return (
              <div key={r.id} className="tide-record">
                <div className="tide-record-dot"></div>
                <div className="tide-record-body">
                  <div className="tide-record-dates">
                    {formatDate(r.start_date)}
                    {r.end_date ? ' — ' + formatDate(r.end_date) : ' — 进行中'}
                  </div>
                  <div className="tide-record-meta">
                    {duration && <span className="tide-record-duration">持续 {duration}</span>}
                    {flowLogs.length >= 2 && (
                      <div className="tide-mini-flow">
                        {flowLogs.map(function(fl, fi) {
                          var h = fl.flow === 'heavy' ? 16 : fl.flow === 'medium' ? 10 : 5;
                          return <div key={fi} className="tide-mini-bar" style={{height: h + 'px'}} title={'D' + (fi+1) + ' ' + fl.flow}></div>;
                        })}
                      </div>
                    )}
                  </div>
                  {r.notes && <div className="tide-record-notes">{r.notes}</div>}
                </div>
                <button className="tide-record-del" onClick={function() { deleteRecord(r.id); }}>×</button>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

export default Tide;
